import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { authService } from '../services/authService';
import { toast } from 'sonner';

const TutorRegister = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: ''
  });

  const handleChange = (e) => {
    let value = e.target.value;
    let name = e.target.name;
    
    if (name === 'phone') {
      value = value.replace(/\D/g, '');
      if (value.startsWith('91') && value.length > 10) {
        value = value.substring(2);
      }
      if (value.length > 10) {
        value = value.slice(0, 10);
      }
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = ['Full name is required'];
    }
    
    if (!formData.email.trim()) {
      newErrors.email = ['Email is required'];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = ['Please enter a valid email address'];
    }
    
    if (!formData.phone) {
      newErrors.phone = ['Phone number is required'];
    } else if (formData.phone.length !== 10) {
      newErrors.phone = ['Phone number must be exactly 10 digits'];
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = ['Please enter a valid Indian mobile number'];
    }
    
    if (!formData.password) {
      newErrors.password = ['Password is required'];
    } else if (formData.password.length < 8) {
      newErrors.password = ['Password must be at least 8 characters'];
    }
    
    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = ['Passwords do not match'];
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    
    const frontendErrors = validateForm();
    if (Object.keys(frontendErrors).length > 0) {
      setErrors(frontendErrors);
      const firstError = Object.values(frontendErrors)[0];
      if (firstError && firstError[0]) {
        toast.error(firstError[0]);
      }
      return;
    }

    setLoading(true);
    try {
      const response = await authService.register(formData);
      toast.success('Registration successful!');
      navigate('/tutor/dashboard');
    } catch (error) {
      const responseData = error.response?.data;
      
      if (responseData?.errors) {
        setErrors(responseData.errors);
        const firstError = Object.values(responseData.errors)[0];
        if (firstError && firstError[0]) {
          toast.error(firstError[0]);
        }
      } else {
        const errorMessage = responseData?.message || 'Registration failed';
        toast.error(errorMessage);
      }
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName] ? errors[fieldName][0] : null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Create Your Account</h2>
          <p className="text-gray-600 mt-2">Join PriEdu and start teaching</p>
        </div>

        <Card className="p-8 bg-white shadow-xl border-gray-100">
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-4 shadow-lg">
              <User className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Join PriEdu</h1>
            <p className="text-gray-600">Start your journey as a tutor</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className={`pl-10 h-12 ${getFieldError('name') ? 'border-red-500 focus:border-red-500' : 'border-gray-200'}`}
                />
              </div>
              {getFieldError('name') && (
                <p className="text-red-500 text-sm mt-1">{getFieldError('name')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  className={`pl-10 h-12 ${getFieldError('email') ? 'border-red-500 focus:border-red-500' : 'border-gray-200'}`}
                />
              </div>
              {getFieldError('email') && (
                <p className="text-red-500 text-sm mt-1">{getFieldError('email')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">
                  +91
                </span>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="9876543210"
                  required
                  maxLength={10}
                  className={`pl-12 h-12 ${getFieldError('phone') ? 'border-red-500 focus:border-red-500' : 'border-gray-200'}`}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Enter 10-digit mobile number</p>
              {getFieldError('phone') && (
                <p className="text-red-500 text-sm mt-1">{getFieldError('phone')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  required
                  minLength={8}
                  className={`pl-10 pr-10 h-12 ${getFieldError('password') ? 'border-red-500 focus:border-red-500' : 'border-gray-200'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {getFieldError('password') && (
                <p className="text-red-500 text-sm mt-1">{getFieldError('password')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  minLength={8}
                  className={`pl-10 h-12 ${getFieldError('password_confirmation') ? 'border-red-500 focus:border-red-500' : 'border-gray-200'}`}
                />
              </div>
              {getFieldError('password_confirmation') && (
                <p className="text-red-500 text-sm mt-1">{getFieldError('password_confirmation')}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white h-12 text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Continue'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/tutor/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200">
                Login here
              </Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TutorRegister;
